import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Button, Spin, Tooltip, message } from 'antd'
import { useHistory } from 'react-router-dom'
import { rolesService } from 'api/configuration/roles'
import { PathRoutes } from 'enum/routes'
import { AccessToLearningProgramsEnum } from 'enum/accessToLearningPrograms'
import { AccessToCoursesEnum } from 'enum/accessToCourses'
import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import createdRoleImg from 'assets/createdRole.png'
import { useRolesAndPermissions } from '../../../context/RolesAndPermissionsContext'
import {
  isDuplicateRoleCode,
  isDuplicateRoleName,
} from '../roleFieldUniqueness'
import { Wrapper, CreatedRoleWrapper, Title, Question } from './styled'

const Buttons = () => {
  const { t } = useTranslation()
  const prefix = 'configuration.rolesAndPermissions.form'
  const history = useHistory()
  const { id: roleId } = useParams<{ id: string }>()
  const isEditing = Boolean(roleId)
  const {
    roleDetail: data,
    roleOptions: roles,
    roleOptionsLoading: loadingRoles,
    roleOptionsLoaded: loadedRoles,
    fetchRoleOptions,
  } = useRolesAndPermissions()
  const [newRoleId, setNewRoleId] = useState(0)
  const [processing, setProcessing] = useState(false)
  const [canContinue, setCanContinue] = useState(false)
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [savingIsComplete, setSavingIsComplete] = useState(false)
  const [alertTitle, setAlertTitle] = useState('')
  const [alertDescriptionPrefix, setAlertDescriptionPrefix] = useState('')
  const [alertDescriptionSuffix, setAlertDescriptionSuffix] = useState('')
  const [alertQuestion, setAlertQuestion] = useState('')
  const [alertConfirmation, setAlertConfirmation] = useState('')
  const [alertCancel, setAlertCancel] = useState('')

  useEffect(() => {
    if (!loadedRoles && !loadingRoles) {
      fetchRoleOptions()
    }
  }, [fetchRoleOptions, loadedRoles, loadingRoles])

  const excludeRoleId = isEditing ? Number(roleId) : undefined

  const hasDuplicateName = useMemo(
    () => isDuplicateRoleName(data.name, roles, excludeRoleId),
    [data.name, roles, excludeRoleId]
  )

  const hasDuplicateCode = useMemo(
    () =>
      data.isDefault
        ? false
        : isDuplicateRoleCode(data.code, roles, excludeRoleId),
    [data.code, data.isDefault, roles, excludeRoleId]
  )

  const getTooltipMessage = () => {
    const hasName = Boolean(data.name.trim().length)
    const hasCode = Boolean(data.code.trim().length)

    if (isEditing && data.isDefault) {
      if (hasName && !hasDuplicateName) {
        return ''
      }
      if (!hasName) {
        return t(`${prefix}.buttons.primary.tooltip.unnamed`)
      }
      if (hasDuplicateName) {
        return t(`${prefix}.buttons.primary.tooltip.duplicateName`)
      }
      return ''
    }

    if (!hasName) {
      return t(`${prefix}.buttons.primary.tooltip.unnamed`)
    }

    if (hasDuplicateName) {
      return t(`${prefix}.buttons.primary.tooltip.duplicateName`)
    }

    if (!hasCode) {
      return t(`${prefix}.buttons.primary.tooltip.uncode`)
    }

    if (hasDuplicateCode) {
      return t(`${prefix}.buttons.primary.tooltip.duplicateCode`)
    }

    if (hasName && !hasDuplicateName && hasCode && !hasDuplicateCode) {
      return t(`${prefix}.buttons.primary.tooltip.noAccess`)
    }

    return ''
  }

  const tooltipMessage = getTooltipMessage()

  useEffect(() => {
    setCanContinue(hasValidConfiguration())
  }, [data, hasDuplicateName, hasDuplicateCode])

  const hasValidConfiguration = () => {
    const hasName = Boolean(data.name.trim().length)
    const hasCode = Boolean(data.code.trim().length)

    if (isEditing && data.isDefault) {
      return hasName && !hasDuplicateName
    }

    const hasAccessToLearningPrograms =
      data.accessToLearningPrograms !== AccessToLearningProgramsEnum.None
    const hasAccessToCourses = data.accessToCourses !== AccessToCoursesEnum.None
    const hasGeneralAccesses = Object.values(data.accesses).some(Boolean)

    if (
      hasName &&
      !hasDuplicateName &&
      hasCode &&
      !hasDuplicateCode &&
      (hasAccessToLearningPrograms || hasAccessToCourses || hasGeneralAccesses)
    )
      return true
    return false
  }

  const showSaveError = (error: unknown) => {
    const apiMessage =
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      typeof (error as { response?: { data?: { errors?: string } } }).response
        ?.data?.errors === 'string'
        ? (error as { response: { data: { errors: string } } }).response.data
            .errors
        : null
    message.error(apiMessage ?? t(`${prefix}.buttons.primary.saveError`))
  }

  const next = () => {
    if (isEditing && data.isDefault) {
      save()
      return
    }

    const hasAccessToLearningPrograms =
      data.accessToLearningPrograms !== AccessToLearningProgramsEnum.None
    const hasAccessToCourses = data.accessToCourses !== AccessToCoursesEnum.None
    const hasGeneralAccesses = Object.values(data.accesses).some(Boolean)

    if (
      hasGeneralAccesses &&
      (hasAccessToLearningPrograms || hasAccessToCourses)
    ) {
      save()
    } else {
      setAlertTitle(
        t(
          `${prefix}.alert.${
            hasGeneralAccesses ? 'noAccess' : 'noGeneralAccess'
          }.title`
        )
      )
      setAlertDescriptionPrefix(
        t(
          `${prefix}.alert.${
            hasGeneralAccesses ? 'noAccess' : 'noGeneralAccess'
          }.descriptionPrefix`
        )
      )
      setAlertDescriptionSuffix(
        t(
          `${prefix}.alert.${
            hasGeneralAccesses ? 'noAccess' : 'noGeneralAccess'
          }.descriptionSuffix`
        )
      )
      setAlertQuestion(
        t(
          `${prefix}.alert.${
            hasGeneralAccesses ? 'noAccess' : 'noGeneralAccess'
          }.question.${isEditing ? 'edit' : 'create'}`
        )
      )
      setAlertConfirmation(
        t(
          `${prefix}.alert.${
            hasGeneralAccesses ? 'noAccess' : 'noGeneralAccess'
          }.confirmation.${isEditing ? 'edit' : 'create'}`
        )
      )
      setAlertCancel(
        t(
          `${prefix}.alert.${
            hasGeneralAccesses ? 'noAccess' : 'noGeneralAccess'
          }.cancel`
        )
      )
      setIsAlertOpen(true)
    }
  }

  const save = async () => {
    setProcessing(true)
    try {
      const response = isEditing
        ? await rolesService.updateRole(roleId, data)
        : await rolesService.saveRole(data)
      saveComplete(response.data.id)
    } catch (error) {
      showSaveError(error)
    } finally {
      setProcessing(false)
    }
  }

  const saveComplete = (value: number) => {
    setNewRoleId(value)
    setIsAlertOpen(false)
    setSavingIsComplete(true)
    void fetchRoleOptions()
  }

  const goBack = () => {
    history.push(PathRoutes.ROLES_AND_PERMISSIONS)
  }

  const later = () => {
    setSavingIsComplete(false)
    history.push(PathRoutes.ROLES_AND_PERMISSIONS)
  }

  const assignRole = () => {
    history.push(
      PathRoutes.ROLES_AND_PERMISSIONS_USERS.replace(
        ':roleId?',
        newRoleId.toString()
      )
    )
  }

  return (
    <>
      <Wrapper>
        <Spin spinning={processing}>
          <Tooltip
            getPopupContainer={() =>
              document.getElementById('lmsPage') as HTMLElement
            }
            title={
              canContinue || !tooltipMessage ? null : (
                <div style={{ width: '161px' }}>{tooltipMessage}</div>
              )
            }
          >
            <span style={{ display: 'inline-block' }}>
              <Button onClick={next} disabled={!canContinue}>
                {isEditing
                  ? t(`${prefix}.buttons.primary.saveChanges`)
                  : t(`${prefix}.buttons.primary.createRole`)}
              </Button>
            </span>
          </Tooltip>
        </Spin>
        <Button onClick={goBack}>{t(`${prefix}.buttons.secondary`)}</Button>
      </Wrapper>
      {isAlertOpen && (
        <Modal
          isOpen={true}
          title={alertTitle}
          isConfirmation={true}
          maxwidth={800}
          maxheight={204}
          close={() => setIsAlertOpen(false)}
          actions={
            <ModalButtons
              primaryText={alertConfirmation}
              primaryAction={save}
              secondaryText={alertCancel}
              secondaryAction={() => setIsAlertOpen(false)}
              processing={processing}
            />
          }
        >
          <>
            <span>{alertDescriptionPrefix}</span>
            <span style={{ fontWeight: 'bold' }}>{` "${data.name}" `}</span>
            <span>{alertDescriptionSuffix}</span>
            <div>{alertQuestion}</div>
          </>
        </Modal>
      )}
      {savingIsComplete && (
        <Modal
          isOpen={true}
          title={t(
            `${prefix}.modal.${isEditing ? 'editedRole' : 'createdRole'}.title`
          )}
          maxwidth={600}
          maxheight={352}
          close={later}
          actions={
            <ModalButtons
              primaryText={t(
                `${prefix}.modal.${
                  isEditing ? 'editedRole' : 'createdRole'
                }.confirmation`
              )}
              primaryAction={assignRole}
              secondaryText={t(
                `${prefix}.modal.${
                  isEditing ? 'editedRole' : 'createdRole'
                }.cancel`
              )}
              secondaryAction={later}
            />
          }
        >
          <CreatedRoleWrapper>
            <img src={createdRoleImg} alt="" />
            <Title>
              {t(
                `${prefix}.modal.${
                  isEditing ? 'editedRole' : 'createdRole'
                }.subtitle`
              )}
            </Title>
            <Question>
              {t(
                `${prefix}.modal.${
                  isEditing ? 'editedRole' : 'createdRole'
                }.question`
              )}
            </Question>
          </CreatedRoleWrapper>
        </Modal>
      )}
    </>
  )
}

export default Buttons
